import { EllipsisVerticalIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Badge } from "@/core/components/ui/badge";
import { Button } from "@/core/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/core/components/ui/dropdown-menu";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";

interface ManualEditsSidebarHeaderProps {
  editCount: number;
  reviewCount: number;
  isRecalculating: boolean;
  canDiscardAll: boolean;
  onDiscardAll: () => void;
  onClose: () => void;
}

// Same shape as the referral and spiff detail sidebars: a tinted block holding a
// badge row, the panel title, and a supporting line — with destructive bulk
// actions tucked into the options menu beside the close control.
export const ManualEditsSidebarHeader = ({
  editCount,
  reviewCount,
  isRecalculating,
  canDiscardAll,
  onDiscardAll,
  onClose,
}: ManualEditsSidebarHeaderProps) => (
  <Stack gap="sm" fullWidth className="shrink-0 bg-bg-secondary/50 p-6">
    <Stack direction="row" justify="between" align="center" fullWidth gap="sm">
      <Typography className="min-w-0 truncate text-2xl font-bold">
        Manual edits
      </Typography>
      <Stack direction="row" gap="sm" align="center" className="shrink-0">
        {canDiscardAll ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="unstyled"
                type="button"
                aria-label="More actions"
              >
                <EllipsisVerticalIcon className="size-6 text-secondary-text" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                className="text-negative-text focus:text-negative-text"
                onClick={onDiscardAll}
              >
                Discard all edits
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
        <Button variant="unstyled" aria-label="Close" onClick={onClose}>
          <XMarkIcon className="size-6 text-secondary-text" />
        </Button>
      </Stack>
    </Stack>

    <Stack
      direction="row"
      gap="sm"
      align="center"
      fullWidth
      className="min-w-0"
    >
      <Typography variant="bodyW1" textColor="secondary" className="min-w-0">
        {editCount === 0
          ? "Nothing edited this pay period"
          : `${editCount} ${editCount === 1 ? "edit" : "edits"} on this pay period`}
      </Typography>
      {reviewCount > 0 ? (
        <Badge variant="gold" size="sm" className="shrink-0">
          {reviewCount} to review
        </Badge>
      ) : null}
      {isRecalculating ? (
        <Badge
          variant="blue"
          size="sm"
          className="shrink-0"
          role="status"
          aria-live="polite"
        >
          Recalculating
        </Badge>
      ) : null}
    </Stack>
  </Stack>
);
